/* eslint-disable @angular-eslint/no-output-native */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { IonInput, IonItem, IonLabel } from '@ionic/angular/standalone';
import { SharedUtilGuid } from '@luggagehero/shared/util';

import { SharedUiSvgIconComponent } from '../svg-icon/shared-ui-svg-icon.component';

@Component({
  selector: 'lh-shared-ui-input',
  imports: [CommonModule, FormsModule, IonInput, IonItem, IonLabel, ReactiveFormsModule, SharedUiSvgIconComponent],
  templateUrl: './shared-ui-input.component.html',
  styleUrl: './shared-ui-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SharedUiInputComponent,
      multi: true,
    },
  ],
  host: { class: 'tw-block' },
})
export class SharedUiInputComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('inputElement') public input?: IonInput | ElementRef<HTMLInputElement>;

  @Output() public readonly focus = new EventEmitter<Event>();
  @Output() public readonly blur = new EventEmitter<Event>();
  @Output() public readonly valueChange = new EventEmitter<string>();

  @Input() public id = SharedUtilGuid.create();
  @Input() public type = 'text';
  @Input() public autocomplete: string | undefined;
  @Input() public placeholder = '';
  @Input() public ariaPlaceholder = '';
  @Input() public label = '';
  @Input() public hasFieldError = false;
  _hideBorders = false;
  get hideBorders(): boolean {
    return this._hideBorders;
  }
  @Input() public set hideBorders(value: boolean) {
    this._hideBorders = value;
    this.cd.markForCheck();
  }
  @Input() public ariaLabel = '';
  /**
   * Marks the control invalid for assistive tech (`aria-invalid`). Absent (`null`) unless passed, so
   * existing callers are unchanged — same opt-in passthrough idiom as `ariaLabel` (AM-4734). Pair
   * with `hasFieldError` for the matching visual state and `ariaDescribedBy` for the error text.
   */
  @Input() public ariaInvalid: boolean | null = null;
  /** Ids of elements that describe this control (e.g. an error paragraph) — renders `aria-describedby`. */
  @Input() public ariaDescribedBy: string | null = null;
  @Input() public floatingLabel = false;
  @Input() public nativeLookAndFeel = false;
  @Input() public required = false;
  @Input() public disabled = false;
  @Input() public tailwind = false;
  @Input() public tabindex: number | undefined;
  @Input() public textAlign: 'left' | 'right' | 'center' = 'left';

  private _value = '';

  public get value(): string {
    return this._value;
  }

  @Input() public set value(_: string) {
    this.writeValue(_);
  }

  private cd = inject(ChangeDetectorRef);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.setDisabledState(this.disabled);
    }
  }

  public onInput(event: Event): void {
    if (this.disabled) {
      return;
    }
    const target = event.target as HTMLInputElement;
    this.writeValue(target.value);
    this.onChange(this.value);
  }

  public onFocus(event: Event): void {
    this.focus.emit(event);
  }

  public onBlur(): void {
    this.onTouched();
  }

  public onChange = (_value: string) => {
    this.valueChange.emit(_value);
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched = () => {};

  public writeValue(value: string): void {
    this._value = value ?? '';
    if (this.input) {
      if (this.input instanceof IonInput) {
        this.input.value = this._value;
      } else {
        this.input.nativeElement.value = this._value;
      }
    }
    this.cd.markForCheck();
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    // If [disabled] input is set, it takes precedence
    if (this.disabled !== undefined) {
      isDisabled = this.disabled;
    }
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  public setFocus(): void {
    if (!this.input) {
      return;
    }
    if (this.input instanceof IonInput) {
      void this.input.setFocus();
    } else {
      this.input.nativeElement.focus();
    }
  }
}
